import React from 'react';

export interface TabPanelTab {
  key: string;
  title: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
  visible?: boolean; // when false, tab is not rendered
}

interface TabPanelProps {
  tabs: TabPanelTab[];
  activeKey: string;
  onChange?: (key: string) => void;
  // Styling hooks
  tabsBarClassName?: string;
  tabButtonClassName?: string;
  activeIndexClassPrefix?: string; // e.g. "design-tabs--active-"
  showIndicator?: boolean;
  contentWrapperClassName?: string;
  contentKey?: string;
  disabledBtnClassName?: string;
}

export const TabPanel: React.FC<TabPanelProps> = ({
  tabs,
  activeKey,
  onChange,
  tabsBarClassName = '',
  tabButtonClassName = '',
  activeIndexClassPrefix,
  showIndicator = false,
  contentWrapperClassName = '',
  contentKey,
  disabledBtnClassName = '',
}) => {
  const visibleTabs = React.useMemo(() => tabs.filter(t => t.visible !== false), [tabs]);
  const enabledVisibleTabs = React.useMemo(() => visibleTabs.filter(t => !t.disabled), [visibleTabs]);
  const activeIndex = Math.max(0, visibleTabs.findIndex(t => t.key === activeKey));
  const activeTab = visibleTabs.find(t => t.key === activeKey) || enabledVisibleTabs[0] || visibleTabs[0] || tabs[0];

  const containerClassName = `${tabsBarClassName} ${activeIndexClassPrefix ? `${activeIndexClassPrefix}${activeIndex}` : ''}`.trim();

  return (
    <>
      <div className="design-tabs-container">
        <div className={containerClassName}>
          {showIndicator && <div className="tab-indicator" />}
          {visibleTabs.map(tab => {
            const isActive = tab.key === activeKey && !tab.disabled;
            const className = `${tabButtonClassName} ${isActive ? 'active' : 'tab--inactive'}`.trim();
            return (
              <button
                key={tab.key}
                className={`${className}${tab.disabled ? ` ${disabledBtnClassName}` : ''}`}
                onClick={() => !tab.disabled && onChange && onChange(tab.key)}
                disabled={!!tab.disabled}
                style={{ opacity: isActive ? 1 : 0.4 }}
              >
                {tab.disabled ? (
                  <svg className="tab-disabled-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <g opacity="0.4">
                    <path d="M4.75 18C4.26875 18 3.85692 17.8416 3.5145 17.5248C3.17208 17.208 3.00058 16.8267 3 16.381V8.28571C3 7.84048 3.1715 7.45946 3.5145 7.14267C3.8575 6.82587 4.26933 6.66721 4.75 6.66667H5.625V5.04762C5.625 3.92778 6.05171 2.97335 6.90512 2.18433C7.75854 1.39532 8.79017 1.00054 10 1C11.2098 0.999461 12.2417 1.39424 13.0957 2.18433C13.9497 2.97443 14.3762 3.92886 14.375 5.04762V6.66667H15.25C15.7312 6.66667 16.1434 6.82533 16.4864 7.14267C16.8294 7.46 17.0006 7.84102 17 8.28571V16.381C17 16.8262 16.8288 17.2075 16.4864 17.5248C16.144 17.8421 15.7318 18.0005 15.25 18H4.75ZM7.375 6.66667H12.625V5.04762C12.625 4.37302 12.3698 3.7996 11.8594 3.32738C11.349 2.85516 10.7292 2.61905 10 2.61905C9.27083 2.61905 8.65104 2.85516 8.14062 3.32738C7.63021 3.7996 7.375 4.37302 7.375 5.04762V6.66667Z" fill="white"/>
                    </g>
                  </svg>
                ) : null}
                {tab.title}
              </button>
            );
          })}
        </div>
      </div>

      <div key={contentKey} className={contentWrapperClassName}>
        {activeTab?.content}
      </div>
    </>
  );
};

export default TabPanel;


